<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import { ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';

  import { Preview } from '@client/pages/bizcards/components';

  import { BizCardsService } from '@client/services/bizcards';
  import { BizCardsRoutingService } from '@client/services/bizcards-routing';

  import { GROUP_TYPES } from '@client/enums';

  import { deepCopy } from '@common/utils/objects';

  export let route = {};
  export let dropIndex = null;
  export let routeIndex = null;
  export let startIndex = null;
  export let reordering = false;
  export let priority = [];
  export let updatePriorityPositions = () => {};
  export let onDelete = () => {};
  export let onRefresh = () => {};
  export let senderGroups = [];
  export let selectedRoute = null;

  /**
   * Whether the page is in selection mode. Renders a checkbox, suppresses the
   * kebab menu, and turns card clicks into toggles.
   * @type {boolean}
   */
  export let selecting = false;

  /**
   * Whether this route is currently selected.
   * @type {boolean}
   */
  export let selected = false;

  /**
   * Called with this route when the card or its checkbox is clicked while selecting.
   * @type {(route: Object) => void}
   */
  export let onSelect = () => {};

  const { basepath } = getContext('config');

  let cache = {};
  let routeStatus = route.status;
  let isUpdating = false;

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const drop = (e) => {
    e.dataTransfer.dropEffect = 'move';
    priority = updatePriorityPositions(priority, startIndex, dropIndex);
    dropIndex = null;
    startIndex = null;
  };

  const dragstart = (e) => {
    if (!reordering) {
      e.preventDefault();
      return false;
    }

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.dropEffect = 'move';

    startIndex = routeIndex;
  };

  const getBusinessCard = async (card) => {
    let email = 'adminuser';

    if (cache[card.blockname]) {
      return cache[card.blockname];
    }

    const match = card.groups?.flatMap(({ senders }) => senders) || [];
    const senders = match.filter(({ username }) => username !== '*');
    const username = match?.length ? (senders.length ? senders[0].username : email) : email;

    const data = {
      username: username,
      scratchblockname: card.blockname,
      scratchtemplate: card.htmltemplate,
      scratchparams: card.params,
      params: card.params,
      prependLines: 0,
      testData: {
        testsender: username
      }
    };

    const response = await BizCardsService.preview(data);
    const { rendered } = response;

    cache[card.blockname] = rendered;

    return rendered;
  };

  const onDeleteHandler = async () => {
    const confirmed = confirm($format('label.DELETE_ROUTE_CONFIRMATION'));

    if (confirmed) {
      isUpdating = true;

      priority = updatePriorityPositions(priority, route.priority, -1);

      const response = await BizCardsRoutingService.deleteRoute(route);

      isUpdating = false;

      if (response?.status >= 400) {
        notify($format('label.FAILURE.DELETE_ROUTE'), 'error');
        return;
      }

      notify($format('label.SUCCESS.DELETE_ROUTE'));

      onDelete(route);
    }
  };

  const onSave = async () => {
    isUpdating = true;

    const selectedGroups = (route.selectedGroups || []).map(({ value }) => value);
    const sendergroups = senderGroups.filter(({ groupname }) => selectedGroups.includes(groupname));
    const { blockname, bizcard = {}, _id } = route;

    const payload = {
      _id,
      blockname,
      priority: route.priority,
      sendergroups,
      status: routeStatus,
      bizcard,
      tags: []
    };

    const response = await BizCardsRoutingService.updateRoute(payload);
    const sgroups = senderGroups.filter(
      ({ groupname }) => sendergroups.filter((group) => groupname === group.groupname).length
    );

    route = {
      ...response,
      ...route,
      selectedGroups: route.selectedGroups || [],
      sendergroups: sgroups,
      status: routeStatus,
      editing: false
    };

    priority = priority.map((item) => (!item._id || item?._id === route._id ? route : item));

    onRefresh();

    isUpdating = false;
  };

  const onEditRoute = () => {
    selectedRoute = deepCopy(route);
  };

  const onDisableRoute = () => {
    routeStatus = false;
    onSave();
  };

  const onenableRoute = () => {
    routeStatus = true;
    onSave();
  };

  // Links and the kebab keep working while selecting, so clicks on them are not toggles.
  const onCardClick = ({ target }) => {
    if (!selecting || target.closest('a, button, input')) {
      return;
    }

    onSelect(route);
  };
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="route"
  class:reordering
  class:disabled={!reordering}
  class:active={reordering && dropIndex === routeIndex}
  class:selecting
  class:border-primary={selected}
  class:bg-primary-subtle={selected}
  draggable={reordering}
  role="button"
  tabindex="0"
  on:dragstart={dragstart}
  on:drop|preventDefault={drop}
  on:dragover|preventDefault
  on:dragenter|preventDefault={() => (dropIndex = routeIndex)}
  on:click={onCardClick}
>
  <Loader backdrop show={isUpdating} />

  {#if reordering}
    <Icon name="grip-vertical" class="position-absolute text-body-tertiary text-large cursor-move" style="left: 8px" />
  {/if}

  {#if selecting}
    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
    <div class="select position-absolute" style="left: 8px" on:click|stopPropagation>
      <input
        class="form-check-input"
        type="checkbox"
        checked={selected}
        on:change={() => onSelect(route)}
        aria-label="{$format('label.SELECT')} {route.blockname}"
      />
    </div>
  {/if}

  <div class="priority-badge" class:active={route.status}>{route.priority}</div>

  <div class="route-section">
    {#key route.blockname}
      <div class="source">
        {#await getBusinessCard( { blockname: route.blockname, groups: route.sendergroups, htmltemplate: route.bizcard?.htmltemplate } )}
          <Preview width={67} height={120} loading />
        {:then html}
          <Preview width={67} height={120} scale={0.18} title={route.blockname} {html} />
        {:catch error}
          <p style="color: red">{error.message}</p>
        {/await}
      </div>
    {/key}
  </div>

  <div class="route-section">
    <b>{$format('label.BUSINESS_CARD')}</b>
    <a
      href="{basepath}/bizcards/studio/{route.blockname}"
      use:link
      style="background-color: {route.color};"
      class="blockname"
    >
      {route.blockname}
    </a>
  </div>
  <div class="route-section">
    <b>{$format('label.GROUPS')}</b>
    {#if route.sendergroups.length}
      <div class="items">
        {#each route.sendergroups as group, index}
          {@const hasGroups = Object.keys(GROUP_TYPES).some((type) => group[type].length)}
          <div class="item" id="tooltip-{route.blockname}-{group.groupname}-{index}">
            <a href="{basepath}/manage/groups/{group.groupname}" use:link>{group.groupname}</a>
            {#if group.senders?.length}
              <Tooltip target="tooltip-{route.blockname}-{group.groupname}-{index}">
                {@html group.senders
                  .map(({ username }) => (username === '*' ? $format('label.ALL_USERS') : username))
                  .sort()
                  .join('<br>') || $format('label.EMPTY')}
              </Tooltip>
            {:else if hasGroups}
              {#each Object.keys(GROUP_TYPES) as type}
                {#if group[type]?.length}
                  {@const groupConfig = GROUP_TYPES[type]}
                  <Tooltip target="tooltip-{route.blockname}-{group.groupname}-{index}">
                    {@html group[type]
                      .map((item) => {
                        const name = Array.isArray(groupConfig.name)
                          ? groupConfig.name.map((value) => item[value]).join(' ')
                          : item[groupConfig.name];
                        return name + (item[groupConfig.count] ? ` (${item[groupConfig.count]})` : '');
                      })
                      .sort()
                      .join('<br>') || $format(groupConfig.genericLabel)}
                  </Tooltip>
                {/if}
              {/each}
            {:else}
              <Tooltip target="tooltip-{route.blockname}-{group.groupname}-{index}">
                {group.userAttrs?.length ? $format('label.DYNAMIC_GROUP') : $format('label.EMPTY')}
              </Tooltip>
            {/if}
          </div>
        {/each}
      </div>
    {:else}
      <div class="muted">{$format('label.NO_ASSIGNED_GROUPS')}</div>
    {/if}
  </div>

  <div class="actions">
    {#if !reordering && !selecting}
      <ButtonDropdown>
        <DropdownToggle color="none" tag="span">
          <Icon name="more-vertical" class="action-icon" />
        </DropdownToggle>
        <DropdownMenu>
          <DropdownItem on:click={onEditRoute}>{$format('label.EDIT')}</DropdownItem>
          {#if route.status}
            <DropdownItem on:click={onDisableRoute}>{$format('label.DISABLE')}</DropdownItem>
          {:else}
            <DropdownItem on:click={onenableRoute}>{$format('label.ENABLE')}</DropdownItem>
          {/if}
          <DropdownItem divider />
          <DropdownItem on:click={onDeleteHandler}>{$format('label.DELETE')}</DropdownItem>
        </DropdownMenu>
      </ButtonDropdown>
    {/if}
  </div>
</div>

<style lang="scss" src="./route.scss"></style>
